import React, { createContext, useContext, useState, ReactNode } from "react";
import { Snackbar, Alert, AlertColor } from "@mui/material";

export interface NotificationOptions {
  message: string;
  severity?: AlertColor;
  duration?: number | null;
  action?: ReactNode;
}

interface NotificationContextType {
  showNotification: (options: NotificationOptions) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return ctx;
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<NotificationOptions>({
    message: "",
    severity: "info",
    duration: 3000,
    action: undefined,
  });

  const showNotification = React.useCallback((opts: NotificationOptions) => {
    setOptions({
      severity: opts.severity ?? "info",
      duration: opts.duration === undefined ? 3000 : opts.duration,
      message: opts.message,
      action: opts.action,
    });
    setOpen(true);
  }, []);

  const hideNotification = React.useCallback(() => setOpen(false), []);

  const contextValue = React.useMemo(
    () => ({ showNotification, hideNotification }),
    [hideNotification, showNotification],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={options.duration}
        onClose={hideNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity={options.severity}
          onClose={hideNotification}
          action={options.action}
        >
          {options.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
